import { AtomRegistry } from 'effect/reactivity';
import { Clock, Effect } from 'effect';
import { apiMutation } from './api-runtime';
import { refreshAudios } from './audio-detail';
import {
  type AudioItem,
  AudioItems,
  type UpdateAudioPayload,
} from './audio-items';
import {
  invalidateAudioLists,
  syncAudio,
  syncedAudio,
  withOptimisticPatch,
} from './audio-store';
import { folderKeys, updateFolderEffect } from './folders';

// Shared audio writes (start / mobile / browser-ext). Each shows its change at
// once through the audio store, stores the server's answer, and refetches only
// the lists the change can reorder or refill. Apps add their own follow-ups
// (toasts, navigation, playback cleanup) on the result.

export type UpdateAudioVariables = {
  readonly audioId: string;
  readonly updates: UpdateAudioPayload;
};

const withUpdates =
  (updates: UpdateAudioPayload) =>
  (audio: AudioItem): AudioItem => ({
    ...audio,
    ...('title' in updates ? { title: updates.title ?? null } : {}),
    ...('description' in updates
      ? { description: updates.description ?? null }
      : {}),
    ...('tags' in updates ? { tags: updates.tags ?? [] } : {}),
    ...('visibility' in updates && audio.sharing
      ? {
          sharing: {
            ...audio.sharing,
            visibility: updates.visibility ?? 'private',
          },
        }
      : {}),
  });

/** PATCHes an audio's metadata (title, description, tags, visibility). */
export const updateAudioAtom = apiMutation(
  ({ audioId, updates }: UpdateAudioVariables) =>
    withOptimisticPatch(
      audioId,
      withUpdates(updates),
      AudioItems.updateEffect(audioId, updates).pipe(
        Effect.tap((audio) => syncAudio(audioId, audio)),
      ),
    ),
);

/**
 * Unarchiving re-converts an archived audio: it leaves every ready list and
 * enters the processing queue as `initializing`, which shows at once. Its
 * detail refetches too, since the conversion replaces its chapters.
 */
export const unarchiveAudioAtom = apiMutation((audioId: string) =>
  Effect.gen(function* () {
    const stamp = yield* Clock.currentTimeMillis;
    yield* withOptimisticPatch(
      audioId,
      (audio) => ({
        ...audio,
        conversion_status: 'initializing',
        created_at_ms: stamp,
        archived_at_ms: null,
      }),
      AudioItems.unarchiveEffect(audioId).pipe(
        Effect.andThen(AudioItems.detailEffect(audioId)),
        Effect.tap((audio) => syncAudio(audioId, audio)),
      ),
    );
    refreshAudios(yield* AtomRegistry.AtomRegistry, [audioId]);
  }),
);

/** Deletes an audio; it leaves every view at once, and the folder counts
 * refetch. */
export const deleteAudioAtom = apiMutation(
  (audioId: string) =>
    withOptimisticPatch(
      audioId,
      () => undefined,
      AudioItems.deleteEffect(audioId).pipe(
        Effect.andThen(syncAudio(audioId, null)),
      ),
    ),
  { reactivityKeys: [folderKeys.list] },
);

/** Records audios as filed in `folderId` (`null` for home), refetching the
 * lists that filter by folder. An audio the client never loaded refetches
 * every list. */
const syncFiled = (audioIds: ReadonlyArray<string>, folderId: string | null) =>
  Effect.forEach(
    audioIds,
    (audioId) =>
      Effect.flatMap(syncedAudio(audioId), (audio) =>
        audio
          ? syncAudio(audioId, { ...audio, folder_id: folderId })
          : invalidateAudioLists,
      ),
    { discard: true },
  );

const folderIdOf = (folderId: string) =>
  folderId === 'root' ? null : folderId;

export type MoveAudioToFolderInput = {
  /** A folder id, or `'root'` for home. */
  readonly folderId: string;
  readonly audioId: string;
};

/** Moves one audio into a folder; it leaves the folder it was in at once and
 * shows at the top of the one it moves to. */
export const moveAudioToFolderAtom = apiMutation(
  ({ folderId, audioId }: MoveAudioToFolderInput) =>
    withOptimisticPatch(
      audioId,
      (audio) => ({ ...audio, folder_id: folderIdOf(folderId) }),
      updateFolderEffect(folderId, { audio_conversion_ids: [audioId] }).pipe(
        Effect.tap(() => syncFiled([audioId], folderIdOf(folderId))),
      ),
    ),
  { reactivityKeys: [folderKeys.list] },
);

export type AddAudiosToFolderInput = {
  readonly folderId: string;
  readonly audioIds: ReadonlyArray<string>;
};

/** Files several audios at once, from the folder picker. */
export const addAudiosToFolderAtom = apiMutation(
  ({ folderId, audioIds }: AddAudiosToFolderInput) =>
    updateFolderEffect(folderId, {
      audio_conversion_ids: [...audioIds],
    }).pipe(Effect.tap(() => syncFiled(audioIds, folderIdOf(folderId)))),
  { reactivityKeys: [folderKeys.list] },
);
